import React from "react";
import { Box, Text, UnstyledButton } from "@mantine/core";
import type { CalendarEvent } from "../../../../../types/transactions";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import { EventFlags, flagCap } from "../../components/Views/EventBlock";
import {
  PlacedBlock,
  blockTimeLabel,
  blockTimeParts,
  processingBand,
} from "./geometry";
import { useMobileFlagsFor } from "./MobileFlagsContext";
import styles from "./MobileBlock.module.css";

export interface MobileBlockProps {
  placed: PlacedBlock;
  variant: "day" | "week";
  ppm: number;
  onTap: (placed: PlacedBlock) => void;
}

/**
 * Lines by block height (px), lowest priority hidden first: the service
 * always shows; the client line from *_TALL; the time line from *_TIME
 * (three lines fit: day 60px = 30 min, week 54px with the time wrapped onto
 * two lines in a narrow column).
 */
const DAY_TALL = 46;
const WEEK_TALL = 38;
const DAY_TIME = 60;
const WEEK_TIME = 54;

/** "$240" with no cents when whole (the design's `$N due` chip). */
const dueAmount = (n: number) => formatCurrency(n).replace(/\.00$/, "");

/**
 * Absolutely positioned block with the desktop event look: 4px service-color
 * bar, 16% tint fill and 42% border of the same hue (CSS color-mix on
 * --svc), service / client / time lines (start – end; compact in week and
 * side-by-side blocks), `$N due` chip, the near-white processing band, and
 * the flag chips (MobileFlagsContext). Time blocks are slate. Settled
 * (completed, or ended and paid) greys out; cancelled/declined dim; ended
 * with a balance turns the overdue fill.
 */
export const MobileBlock: React.FC<MobileBlockProps> = ({
  placed,
  variant,
  ppm,
  onTap,
}) => {
  const { appt, col, cols, top, height } = placed;
  const split = cols > 1;
  const week = variant === "week";
  const inset = week ? 1 : 2;
  const band = processingBand(appt);
  const tall = height >= (week ? WEEK_TALL : DAY_TALL);
  // Week columns are too narrow for a time once split side by side.
  const timed = week ? !split && height >= WEEK_TIME : height >= DAY_TIME;
  const timeParts = blockTimeParts(appt);

  // Chips where they fit: tall day blocks, unsplit timed week blocks.
  const flagsFor = useMobileFlagsFor();
  const showFlags =
    !!flagsFor && !appt.isBlock && (week ? timed : tall) && !appt.dim;
  const flags =
    showFlags && !(appt.event as CalendarEvent).isTransient
      ? flagsFor(appt.event)
      : [];

  // First in the text column: the chips float bottom-right and the lines
  // beside them truncate.
  const chips = flags.length > 0 && (
    <EventFlags
      fit
      float
      flags={flags}
      max={flagCap(variant, cols)}
      muted={appt.settled}
    />
  );

  const dueText =
    appt.due > 0 && !week
      ? split
        ? dueAmount(appt.due)
        : `${dueAmount(appt.due)} due`
      : "";

  return (
    <UnstyledButton
      className={styles.block}
      data-block="true"
      data-variant={variant}
      data-split={split}
      data-settled={appt.settled}
      data-dim={appt.dim}
      data-overdue={appt.overdue}
      data-time-block={appt.isBlock || undefined}
      data-testid={`mobile-block-${appt.id}`}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        onTap(placed);
      }}
      style={{
        top,
        height,
        left: `calc(${(col / cols) * 100}% + ${inset}px)`,
        width: `calc(${100 / cols}% - ${inset * 2}px)`,
        zIndex: 5 + col,
        ...({ "--svc": appt.hue } as React.CSSProperties),
      }}
    >
      {band && (
        <Box
          className={styles.band}
          data-testid="mobile-block-processing"
          style={{
            top: band.offsetMin * ppm,
            height: band.minutes * ppm,
          }}
        />
      )}
      <Box className={styles.bar} />
      {week ? (
        <Box className={styles.body}>
          {chips}
          <Text span className={styles.weekService}>
            {appt.service}
          </Text>
          {tall && (
            <Text span className={styles.weekClient}>
              {appt.client}
            </Text>
          )}
          {timed && (
            <Text
              span
              className={styles.weekTime}
              data-testid="mobile-block-time"
            >
              <span className={styles.timePart}>{timeParts[0]}</span>
              <span className={styles.timePart}>{timeParts[1]}</span>
            </Text>
          )}
        </Box>
      ) : (
        <Box className={styles.body} data-tall={tall} data-timed={timed}>
          {chips}
          <Box className={styles.topRow}>
            <Text span className={styles.service}>
              {appt.service}
            </Text>
            {dueText && (
              <Text span className={styles.due}>
                {dueText}
              </Text>
            )}
          </Box>
          {tall && (
            <Text span className={styles.client}>
              {appt.client}
            </Text>
          )}
          {timed && (
            <Text span className={styles.time} data-testid="mobile-block-time">
              {split ? timeParts.join("") : blockTimeLabel(appt)}
            </Text>
          )}
        </Box>
      )}
    </UnstyledButton>
  );
};
